import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Settings, Plus, X as XIcon, ChevronUp, ChevronDown, ArrowUpRight } from 'lucide-react';
import {
  SiInstagram, SiTiktok, SiFacebook, SiSnapchat, SiPinterest, SiThreads, SiYoutube,
} from 'react-icons/si';
import { FaTwitter } from 'react-icons/fa6';

// Curated platforms only — members supply a handle, never a raw URL, so a
// link can only ever point at the platform's own domain.
const SOCIAL_PLATFORMS = {
  instagram: { label: 'Instagram', urlTemplate: 'https://instagram.com/{username}', Icon: SiInstagram, color: '#E4405F' },
  twitter: { label: 'Twitter', urlTemplate: 'https://x.com/{username}', Icon: FaTwitter, color: '#1DA1F2' },
  tiktok: { label: 'TikTok', urlTemplate: 'https://tiktok.com/@{username}', Icon: SiTiktok, color: '#000000' },
  facebook: { label: 'Facebook', urlTemplate: 'https://facebook.com/{username}', Icon: SiFacebook, color: '#1877F2' },
  snapchat: { label: 'Snapchat', urlTemplate: 'https://snapchat.com/add/{username}', Icon: SiSnapchat, color: '#FFFC00', darkIcon: true },
  pinterest: { label: 'Pinterest', urlTemplate: 'https://pinterest.com/{username}', Icon: SiPinterest, color: '#BD081C' },
  threads: { label: 'Threads', urlTemplate: 'https://threads.net/@{username}', Icon: SiThreads, color: '#000000' },
  // Admin-only for now (also enforced in backend/routes/profile_widgets.py).
  youtube: { label: 'YouTube', urlTemplate: 'https://youtube.com/@{username}', Icon: SiYoutube, color: '#FF0000', adminOnly: true },
  upscrolled: {
    label: 'Upscrolled',
    urlTemplate: 'https://upscrolled.com/@{username}',
    iconUrl: 'https://jlucsghffgpjtcitotco.supabase.co/storage/v1/object/public/post-images/Upscrolled-icon.png',
    color: '#4AADA8',
  },
};

const PLATFORM_ORDER = Object.keys(SOCIAL_PLATFORMS);

// Accepts a bare handle, an "@handle", or a pasted full profile URL, and
// always reduces it down to just the handle.
const cleanUsername = (raw) => {
  let v = (raw || '').trim();
  if (!v) return '';
  if (/^https?:\/\//i.test(v)) {
    try {
      const u = new URL(v);
      const parts = u.pathname.split('/').filter(Boolean);
      v = parts[parts.length - 1] || v;
    } catch { /* not a valid URL — fall through and treat as typed */ }
  }
  return v.replace(/^@/, '').trim();
};

const buildUrl = (platform, username) => {
  const def = SOCIAL_PLATFORMS[platform];
  if (!def) return '#';
  return def.urlTemplate.replace('{username}', encodeURIComponent(username));
};

const GLASS_TILE_BG = 'rgba(255,255,255,0.18)';

const CARD_THEMES = {
  brand: { label: 'Lavender Teal', cardBg: 'linear-gradient(135deg, #A99CC8 0%, #4AADA8 100%)' },
  glass: {
    label: 'Glass',
    cardBg: 'linear-gradient(135deg, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0.15) 100%)',
    blur: 'backdrop-blur-xl',
    border: '1px solid rgba(255,255,255,0.4)',
  },
};
const getCardTheme = (key) => CARD_THEMES[key] || CARD_THEMES.brand;

const PlatformIcon = ({ platform, dark, size = 22 }) => {
  const def = SOCIAL_PLATFORMS[platform];
  if (!def) return null;
  const color = dark ? '#1a1a1a' : '#ffffff';
  if (def.iconUrl) {
    return <img src={def.iconUrl} alt="" aria-hidden="true" style={{ width: size, height: size, objectFit: 'contain' }} />;
  }
  if (def.Icon) {
    const Icon = def.Icon;
    return <Icon size={size} color={color} aria-hidden="true" />;
  }
  return <span aria-hidden="true" className="font-bold" style={{ fontSize: size * 0.7, color }}>{def.monogram}</span>;
};

// The first link in the member's order is "featured": a wide row showing the
// platform name and handle. The rest are compact icon tiles in a 3-wide grid
// beneath it. The card grows in height with the number of links. Tiles are
// always rendered in place (no hover-to-reveal) so it works the same on touch;
// hover only swaps a tile's glass background for its brand color.
const tileBase = 'transition-[background-color,transform,box-shadow,border-color] duration-300 ease-out hover:-translate-y-0.5 active:scale-[0.97] focus-visible:outline focus-visible:outline-2 focus-visible:outline-white';

const tileStyle = (def, hovered, featured) => ({
  background: hovered ? def.color : (featured ? 'rgba(255,255,255,0.22)' : GLASS_TILE_BG),
  border: `1px solid ${hovered ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.35)'}`,
  boxShadow: hovered ? '0 8px 18px -6px rgba(20,40,60,0.45)' : '0 2px 8px rgba(0,0,0,0.08)',
});

const FeaturedTile = ({ link }) => {
  const [hovered, setHovered] = useState(false);
  const def = SOCIAL_PLATFORMS[link.platform];
  if (!def) return null;
  const dark = hovered && def.darkIcon;
  return (
    <a
      href={buildUrl(link.platform, link.username)}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`col-span-3 flex items-center gap-3 p-2.5 rounded-arch backdrop-blur-md min-h-[56px] ${dark ? 'text-gray-900' : 'text-white'} ${tileBase}`}
      style={tileStyle(def, hovered, true)}
      aria-label={`${def.label}: @${link.username}`}
      data-testid={`social-link-${link.platform}`}
    >
      <span className="w-10 h-10 shrink-0 rounded-2xl bg-white/20 flex items-center justify-center">
        <PlatformIcon platform={link.platform} dark={dark} size={22} />
      </span>
      <span className="flex flex-col min-w-0 flex-1">
        <span className="font-heading font-semibold text-[13px] leading-tight">{def.label}</span>
        <span className="text-[11px] opacity-80 truncate">@{link.username}</span>
      </span>
      <ArrowUpRight className="w-3.5 h-3.5 shrink-0 opacity-60" aria-hidden="true" />
    </a>
  );
};

const SocialTile = ({ link }) => {
  const [hovered, setHovered] = useState(false);
  const def = SOCIAL_PLATFORMS[link.platform];
  if (!def) return null;
  return (
    <a
      href={buildUrl(link.platform, link.username)}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`aspect-square min-h-[44px] rounded-arch backdrop-blur-md flex items-center justify-center ${tileBase}`}
      style={tileStyle(def, hovered, false)}
      aria-label={`${def.label}: @${link.username}`}
      title={`${def.label}: @${link.username}`}
      data-testid={`social-link-${link.platform}`}
    >
      <PlatformIcon platform={link.platform} dark={hovered && def.darkIcon} size={22} />
    </a>
  );
};

const SocialsSettings = ({ config, isAdmin, onCancel, onSave }) => {
  const [links, setLinks] = useState(config.links || []);
  const [theme, setTheme] = useState(config.theme || 'brand');
  const [addPlatform, setAddPlatform] = useState('');
  const [addUsername, setAddUsername] = useState('');

  const available = PLATFORM_ORDER.filter(
    (p) => (isAdmin || !SOCIAL_PLATFORMS[p].adminOnly) && !links.some((l) => l.platform === p)
  );

  const handleAdd = () => {
    const username = cleanUsername(addUsername);
    if (!addPlatform || !username) return;
    setLinks((prev) => [...prev, { platform: addPlatform, username }]);
    setAddPlatform('');
    setAddUsername('');
  };

  const handleRemove = (platform) => setLinks((prev) => prev.filter((l) => l.platform !== platform));

  const handleMove = (index, dir) => {
    setLinks((prev) => {
      const next = [...prev];
      const swapWith = index + dir;
      if (swapWith < 0 || swapWith >= next.length) return prev;
      [next[index], next[swapWith]] = [next[swapWith], next[index]];
      return next;
    });
  };

  return (
    <div className="w-[220px] rounded-3xl p-4 bg-white dark:bg-gray-800 shadow-2xl border border-white/10" data-testid="socials-widget-settings">
      <p className="text-xs font-semibold text-text-main mb-2">Social Links</p>

      {links.length > 0 && (
        <div className="space-y-1.5 mb-3">
          {links.map((l, i) => (
            <div key={l.platform} className="flex items-center justify-between gap-1.5 px-2 py-1.5 rounded-arch bg-black/5 dark:bg-white/10">
              <span className="text-xs text-text-main truncate">{SOCIAL_PLATFORMS[l.platform]?.label}: @{l.username}</span>
              <div className="flex items-center shrink-0">
                <button
                  onClick={() => handleMove(i, -1)}
                  disabled={i === 0}
                  aria-label={`Move ${SOCIAL_PLATFORMS[l.platform]?.label} up`}
                  className="text-text-muted hover:text-primary disabled:opacity-25 disabled:hover:text-text-muted transition-colors min-h-[44px] min-w-[26px] flex items-center justify-center -my-2.5"
                >
                  <ChevronUp className="w-3.5 h-3.5" aria-hidden="true" />
                </button>
                <button
                  onClick={() => handleMove(i, 1)}
                  disabled={i === links.length - 1}
                  aria-label={`Move ${SOCIAL_PLATFORMS[l.platform]?.label} down`}
                  className="text-text-muted hover:text-primary disabled:opacity-25 disabled:hover:text-text-muted transition-colors min-h-[44px] min-w-[26px] flex items-center justify-center -my-2.5"
                >
                  <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />
                </button>
                <button
                  onClick={() => handleRemove(l.platform)}
                  aria-label={`Remove ${SOCIAL_PLATFORMS[l.platform]?.label}`}
                  className="text-text-muted hover:text-red-500 transition-colors min-h-[44px] min-w-[30px] flex items-center justify-center -my-2.5 -mr-1"
                >
                  <XIcon className="w-3.5 h-3.5" aria-hidden="true" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {available.length > 0 ? (
        <div className="p-2 rounded-arch bg-black/5 dark:bg-white/10 mb-3 space-y-1.5">
          <label className="sr-only" htmlFor="social-platform-select">Platform</label>
          <select
            id="social-platform-select"
            value={addPlatform}
            onChange={(e) => setAddPlatform(e.target.value)}
            className="w-full px-2.5 py-1.5 rounded-arch text-xs border border-primary/20 bg-white/70 dark:bg-white dark:text-gray-800"
          >
            <option value="">Choose a platform…</option>
            {available.map((p) => <option key={p} value={p}>{SOCIAL_PLATFORMS[p].label}</option>)}
          </select>
          <label className="sr-only" htmlFor="social-username-input">Username</label>
          <input
            id="social-username-input"
            value={addUsername}
            onChange={(e) => setAddUsername(e.target.value)}
            placeholder="username"
            className="w-full px-2.5 py-1.5 rounded-arch text-xs border border-primary/20 bg-white/70 dark:bg-white dark:text-gray-800"
          />
          <button
            onClick={handleAdd}
            disabled={!addPlatform || !cleanUsername(addUsername)}
            className="w-full flex items-center justify-center gap-1 py-1.5 rounded-arch text-[11px] font-medium bg-primary text-white disabled:opacity-50"
          >
            <Plus className="w-3 h-3" aria-hidden="true" /> Add
          </button>
        </div>
      ) : (
        <p className="text-[11px] text-text-muted mb-3">All platforms added.</p>
      )}

      <div className="mb-3">
        <p className="text-[11px] text-text-muted mb-1.5">Color</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(CARD_THEMES).map(([key, t]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTheme(key)}
              title={t.label}
              aria-label={t.label}
              className={`w-7 h-7 rounded-full border-2 transition-transform ${theme === key ? 'scale-110 border-primary' : 'border-black/10 dark:border-white/20'}`}
              style={{ background: t.cardBg }}
            />
          ))}
        </div>
      </div>

      <div className="flex gap-2">
        <button onClick={onCancel} className="flex-1 py-2 rounded-arch text-xs font-medium text-text-muted hover:bg-black/5 dark:hover:bg-white/10 transition-colors">
          Cancel
        </button>
        <button onClick={() => onSave({ links, theme })} className="flex-1 py-2 rounded-arch text-xs font-medium bg-primary text-white">
          Save
        </button>
      </div>
    </div>
  );
};

const SocialsWidget = ({ config = {}, editMode, onUpdateConfig, profileData }) => {
  const [showSettings, setShowSettings] = useState(false);
  const links = config.links || [];

  if (showSettings) {
    return (
      <SocialsSettings
        config={config}
        isAdmin={!!profileData?.is_admin}
        onCancel={() => setShowSettings(false)}
        onSave={(next) => { onUpdateConfig(next); setShowSettings(false); }}
      />
    );
  }

  if (links.length === 0) {
    if (!editMode) return null;
    return (
      <button
        onClick={() => setShowSettings(true)}
        className="w-[220px] aspect-square rounded-3xl flex flex-col items-center justify-center gap-2 border-2 border-dashed border-white/30 text-white/70 hover:text-white hover:border-white/50 transition-colors"
        data-testid="socials-widget-empty"
      >
        <Plus className="w-6 h-6" aria-hidden="true" />
        <span className="text-xs">Add your socials</span>
      </button>
    );
  }

  const cardTheme = getCardTheme(config.theme);
  const [featured, ...rest] = links;

  return (
    <motion.div
      className={`relative w-[220px] rounded-3xl p-3 shadow-[0_0_20px_rgba(0,0,0,0.2)] ${cardTheme.blur || ''}`}
      style={{ background: cardTheme.cardBg, border: cardTheme.border }}
      initial={{ opacity: 0, filter: 'blur(10px)' }}
      animate={{ opacity: 1, filter: 'blur(0px)' }}
      transition={{ duration: 0.3, ease: 'easeInOut', type: 'spring' }}
      data-testid="socials-widget"
    >
      <div className="flex items-center justify-between px-1 pt-0.5 mb-2.5 min-h-[28px]">
        <p className="text-base font-bold font-heading text-white [text-shadow:0_1px_4px_rgba(0,0,0,0.25)]">Socials</p>
        {editMode && (
          <button
            onClick={() => setShowSettings(true)}
            className="min-h-[44px] min-w-[44px] -my-2 -mr-2 flex items-center justify-center rounded-full text-white hover:bg-black/10 transition-colors"
            aria-label="Edit social links"
            data-testid="socials-widget-edit-btn"
          >
            <Settings className="w-4 h-4" aria-hidden="true" />
          </button>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <FeaturedTile link={featured} />
        {rest.map((l) => <SocialTile key={l.platform} link={l} />)}
      </div>
    </motion.div>
  );
};

export default SocialsWidget;
